/**
 * Supabase Client Observability
 *
 * Provides OpenTelemetry instrumentation for Supabase client requests.
 * Adds auth context (auth_type, client_type) that is not available in
 * Supabase logs or existing @vercel/otel/fetch traces.
 *
 * Trace hierarchy:
 *   web.request (parent)
 *   └── supabase.request (this span - adds auth context)
 *       └── fetch.GET (created by @vercel/otel/fetch - has http.* attributes)
 */

import { SpanStatusCode, trace } from '@opentelemetry/api';
import { AUTH_DOMAIN, getErrorMessage } from './shared';

const tracer = trace.getTracer('magicschoolai.supabase');

export { AUTH_DOMAIN, getErrorMessage } from './shared';

export type SupabaseAuthType = 'service_role' | 'anon';
export type SupabaseClientType = 'server' | 'browser' | 'middleware';
export type SupabaseKeySource = 'SUPABASE_SECRET_KEY' | 'NEXT_PUBLIC_SUPABASE_PUBLISHABLE_KEY' | 'unset';

type FetchFunction = (input: string | URL | Request, init?: RequestInit) => Promise<Response>;

interface SupabaseClientSpanOptions {
  authType: SupabaseAuthType;
  clientType: SupabaseClientType;
  /** Optional custom fetch function to wrap with tracing. Defaults to global fetch. */
  customFetch?: FetchFunction;
  /** The env var name that resolved the Supabase API key, for APM migration tracking. */
  keySource: SupabaseKeySource;
}

/**
 * Enriches the currently active OTEL span with user/org/teacher identity.
 * Called from fetchFullUser after user identity is resolved. In web requests
 * this is the root web.request span; in Trigger.dev workers it's the active
 * job span. No-op when called outside any OTEL context (e.g. build phase).
 *
 * WARNING: user.id, org.id, teacher.id, and student.id are high/medium cardinality.
 * Never use them as group-by dimensions in Datadog custom metrics —
 * use user.role or user.plan instead for aggregation.
 */
export function setUserContextOnActiveSpan(
  user: { id: string; org_id: string | null; user_role: string | null; subscriptionPlanName: 'FREE' | 'PLUS' | 'PARTNER' },
  teacherId?: string,
  studentId?: string,
): void {
  const span = trace.getActiveSpan();
  if (!span) return;
  span.setAttributes({
    'user.id': user.id,
    'user.role': user.user_role ?? 'unknown',
    'user.plan': user.subscriptionPlanName,
    ...(user.org_id ? { 'org.id': user.org_id } : {}),
    ...(teacherId ? { 'teacher.id': teacherId } : {}),
    ...(studentId ? { 'student.id': studentId } : {}),
  });
}

/**
 * Sets student.id on the active OTEL span. Called from student auth paths
 * (withStudentAuthBase, withStudentQuizAuthBase) which do not go through
 * fetchFullUser and therefore never hit setUserContextOnActiveSpan.
 *
 * WARNING: student.id is high cardinality — do not use as a group-by
 * dimension in Datadog custom metrics.
 */
export function setStudentIdOnActiveSpan(studentId: string): void {
  const span = trace.getActiveSpan();
  if (!span) return;
  span.setAttribute('student.id', studentId);
}

/**
 * Creates a traced fetch function for Supabase clients.
 *
 * IMPORTANT: This wraps the existing @vercel/otel/fetch instrumentation.
 * The fetch span created by @vercel/otel will be a child of our supabase.request span.
 *
 * This layered approach:
 * 1. Preserves all existing http.* attributes from @vercel/otel/fetch
 * 2. Adds auth-specific context (auth_type, client_type) - NOT available elsewhere
 * 3. Enables dashboard filtering by auth.domain
 *
 * NOTE: We intentionally do NOT add supabase.table here because:
 * - APM traces already have http.path_group from @vercel/otel/fetch
 * - Supabase edge_logs already contain the full URL with table name
 * - Adding it here would be redundant data
 */
export {
  type JwtMethod,
  type JwtOperation,
  type JwtTokenType,
  withJwtSpan,
} from './jwt-tracing';

export function createTracedFetch(options: SupabaseClientSpanOptions): FetchFunction {
  const fetchFn = options.customFetch ?? fetch;
  return (input: string | URL | Request, init?: RequestInit): Promise<Response> => {
    return tracer.startActiveSpan('supabase.request', async (span) => {
      span.setAttribute('auth.domain', AUTH_DOMAIN);
      span.setAttribute('auth.operation_type', 'supabase_request');

      span.setAttribute('supabase.auth_type', options.authType);
      span.setAttribute('supabase.client_type', options.clientType);
      span.setAttribute('supabase.key_source', options.keySource);

      try {
        const response = await fetchFn(input, init);
        span.setAttribute('supabase.success', response.ok);
        span.setAttribute('supabase.http_status', response.status);
        // Only mark 5xx responses as errors to avoid inflating error rates with 4xx client errors
        if (response.status >= 500) {
          span.setStatus({ code: SpanStatusCode.ERROR });
        } else {
          span.setStatus({ code: SpanStatusCode.OK });
        }
        return response;
      } catch (error) {
        span.setAttribute('supabase.success', false);
        span.setAttribute('supabase.error_type', 'network_error');
        span.setStatus({ code: SpanStatusCode.ERROR, message: getErrorMessage(error) });
        span.recordException(error instanceof Error ? error : new Error(String(error)));
        throw error;
      } finally {
        span.end();
      }
    });
  };
}
